import { useState } from "react";
import { Play, Youtube, Facebook, ExternalLink } from "lucide-react";
import { PARISH } from "@/lib/constants";
import { TIDS } from "@/constants/testIds";

const Card = ({ children }) => (
    <div className="rounded-2xl border border-maroon-100 bg-white/85 overflow-hidden shadow-sm">
        {children}
    </div>
);

const YouTubeBlock = () => {
    const [load, setLoad] = useState(false);
    // The `live_stream?channel=` embed only shows something when a live broadcast is
    // actually running, otherwise the iframe is blank. Fall back to the channel's
    // "uploads" playlist (channel-id UC... → playlist-id UU...) so the latest video
    // always plays when parishioners click "Load live stream" even outside Mass times.
    const uploadsPlaylist = PARISH.youtubeChannelId
        ? "UU" + PARISH.youtubeChannelId.replace(/^UC/, "")
        : "";
    const channelEmbed = uploadsPlaylist
        ? `https://www.youtube-nocookie.com/embed/videoseries?list=${uploadsPlaylist}&autoplay=1&rel=0`
        : `https://www.youtube-nocookie.com/embed/live_stream?channel=${PARISH.youtubeChannelId}&autoplay=1`;

    return (
        <Card>
            <div className="flex items-center justify-between p-5">
                <div className="flex items-center gap-2">
                    <Youtube className="text-maroon-600" size={20} />
                    <h3 className="font-serif text-xl text-maroon-800">YouTube</h3>
                </div>
                <a
                    href={PARISH.socials.youtube}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-maroon-700/80 hover:text-maroon-800 inline-flex items-center gap-1"
                >
                    Open channel <ExternalLink size={12} />
                </a>
            </div>
            <div className="gold-divider opacity-50" />
            <div className="relative aspect-video bg-maroon-900">
                {!load ? (
                    <button
                        data-testid={TIDS.live.loadYoutube}
                        onClick={() => setLoad(true)}
                        className="group absolute inset-0 flex items-center justify-center"
                        aria-label="Load YouTube stream"
                    >
                        <div className="absolute inset-0 bg-gradient-to-br from-maroon-700 via-maroon-800 to-maroon-900" />
                        <div className="relative flex flex-col items-center text-cream-50">
                            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-cream-50/15 backdrop-blur-md ring-2 ring-gold-300/60 transition-transform group-hover:scale-105">
                                <Play size={28} fill="currentColor" />
                            </div>
                            <span className="mt-3 text-sm font-semibold tracking-wide">Load live stream</span>
                            <span className="text-xs text-cream-50/70 mt-0.5">YouTube · @{PARISH.youtubeChannelHandle}</span>
                        </div>
                    </button>
                ) : (
                    <iframe
                        data-testid={TIDS.live.youtubeEmbed}
                        title="Holy Trinity YouTube"
                        src={channelEmbed}
                        loading="lazy"
                        className="absolute inset-0 h-full w-full"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        referrerPolicy="strict-origin-when-cross-origin"
                        allowFullScreen
                    />
                )}
            </div>
        </Card>
    );
};

const FacebookBlock = () => {
    const [load, setLoad] = useState(false);
    const fbEmbed = `https://www.facebook.com/plugins/page.php?href=${encodeURIComponent(
        PARISH.socials.facebook
    )}&tabs=timeline&width=500&height=600&small_header=true&adapt_container_width=true&hide_cover=false&show_facepile=true`;

    return (
        <Card>
            <div className="flex items-center justify-between p-5">
                <div className="flex items-center gap-2">
                    <Facebook className="text-maroon-600" size={20} />
                    <h3 className="font-serif text-xl text-maroon-800">Facebook</h3>
                </div>
                <a
                    href={PARISH.socials.facebook}
                    target="_blank"
                    rel="noreferrer"
                    className="text-xs text-maroon-700/80 hover:text-maroon-800 inline-flex items-center gap-1"
                >
                    Open page <ExternalLink size={12} />
                </a>
            </div>
            <div className="gold-divider opacity-50" />
            <div className="relative bg-cream-50 min-h-[480px]">
                {!load ? (
                    <button
                        data-testid={TIDS.live.loadFacebook}
                        onClick={() => setLoad(true)}
                        className="group absolute inset-0 flex items-center justify-center"
                    >
                        <div className="absolute inset-0 bg-gradient-to-br from-cream-200 to-cream-50" />
                        <div className="relative flex flex-col items-center text-maroon-800">
                            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-maroon-600 text-cream-50 ring-2 ring-gold-300/60 transition-transform group-hover:scale-105">
                                <Facebook size={28} />
                            </div>
                            <span className="mt-3 text-sm font-semibold tracking-wide">Load Facebook feed</span>
                            <span className="text-xs text-maroon-700/70 mt-0.5">@{PARISH.facebookPageName}</span>
                        </div>
                    </button>
                ) : (
                    <iframe
                        data-testid={TIDS.live.facebookEmbed}
                        title="Holy Trinity Facebook"
                        src={fbEmbed}
                        loading="lazy"
                        className="absolute inset-0 h-full w-full"
                        scrolling="no"
                        frameBorder="0"
                        allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                    />
                )}
            </div>
        </Card>
    );
};

const LiveStream = () => {
    return (
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14">
            <span className="eyebrow">Stream</span>
            <h1 className="heading-display mt-2">Live Stream & Replays</h1>
            <p className="mt-4 max-w-2xl text-maroon-700/85">
                Pray with us from wherever you are. Our Masses, special celebrations and parish events
                are streamed on YouTube and shared on our Facebook page.
            </p>

            <div className="mt-8 grid gap-6 lg:grid-cols-2">
                <YouTubeBlock />
                <FacebookBlock />
            </div>

            <p className="mt-6 text-xs text-maroon-700/70 italic">
                Players load only when you click, keeping the page fast and saving your data.
            </p>
        </div>
    );
};

export default LiveStream;
